import { reactular } from '@vendhq/reactular'
import { AblyProvider, ChannelProvider } from 'ably/react'
import React, { useEffect } from 'react'
import {
  getTerminalStatusChannelName,
  initTerminalStatusLogger,
} from '@lightspeed-payments/terminal-status'

import { useAppSelector } from '../../state/hooks'
import {
  selectActiveOutletId,
  selectActiveRegisterId,
} from '../../state/slices/posState/posStateSlice'
import { getFeatureFromCache } from '../../util/feature-cache'
import { useVdFeatureManager } from '../../ng-compat/hooks'
import { TerminalStatusEventHandler } from '../payments/terminal-status/terminal-status-event-handler'
import {
  LSPAY_DATADOG_CLIENT_TOKEN,
  SANDBOX_FLAG,
} from '../payments/lspay-payment-modal/helpers/lspayConstants'
import { SaleChangeEvents } from '../sale/sale-change-listener/sale-change-listener'
import { RealtimeProvider, useRealtimeProvider } from './RealtimeProvider'

const FEATURE_CFD_ENABLED = 'lspay_customer_facing_display_enabled'
const FEATURE_TERMINAL_STATUS_ENABLED = 'lspay_terminal_status_enabled'

const RealtimeAblyEventHandler: React.FC = () => {
  const { client, serialNumber } = useRealtimeProvider()
  const cfdFeatureEnabled = getFeatureFromCache(FEATURE_CFD_ENABLED)
  const terminalStatusFeatureEnabled = getFeatureFromCache(
    FEATURE_TERMINAL_STATUS_ENABLED
  )

  if (!client || !serialNumber) {
    return null
  }

  const channelName = getTerminalStatusChannelName(serialNumber)

  return (
    <AblyProvider client={client}>
      <ChannelProvider channelName={channelName}>
        {terminalStatusFeatureEnabled && <TerminalStatusEventHandler />}
        {cfdFeatureEnabled && <SaleChangeEvents channelName={channelName} />}
      </ChannelProvider>
    </AblyProvider>
  )
}

export const RealtimeEventHandler: React.FC = () => {
  const outletId = useAppSelector(selectActiveOutletId)
  const registerId = useAppSelector(selectActiveRegisterId)

  const isSandbox = useVdFeatureManager().get(SANDBOX_FLAG)
  const cfdFeatureEnabled = getFeatureFromCache(FEATURE_CFD_ENABLED)
  const terminalStatusFeatureEnabled = getFeatureFromCache(
    FEATURE_TERMINAL_STATUS_ENABLED
  )

  /**
   * Terminal Status Logger Initialization
   */
  useEffect(() => {
    if (!outletId || !registerId || !terminalStatusFeatureEnabled) {
      return
    }

    initTerminalStatusLogger({
      service: 'webregister',
      clientToken: LSPAY_DATADOG_CLIENT_TOKEN,
      env: isSandbox ? 'stage' : 'prod',
      product: 'x-series',
      pli: 'lsx:' + outletId,
      verbose: isSandbox,
    })
  }, [outletId, registerId, isSandbox, terminalStatusFeatureEnabled])

  // Add more features here as they are added
  const allFeaturesDisabled =
    !cfdFeatureEnabled && !terminalStatusFeatureEnabled

  if (!outletId || !registerId || allFeaturesDisabled) {
    return null
  }

  return (
    <RealtimeProvider outletId={outletId} registerId={registerId}>
      <RealtimeAblyEventHandler />
    </RealtimeProvider>
  )
}

export const ReactRealtimeEventHandlerComponent = reactular(
  RealtimeEventHandler,
  [],
  'reactWrapper'
)
